"use client";

/**
 * Programs (หลักสูตร) — multi-tenant root collection
 *
 * Path: /programs/{programId}
 *
 * Each program is a self-contained tenant with its own courses, curriculum,
 * verifications, plans, and committee. Users belong to one or more programs
 * via the `programs[]` array on their user doc.
 */
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  serverTimestamp, Timestamp, where, query, orderBy,
} from "firebase/firestore";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getFirebase } from "./client";

// ============ Types ============

export interface ProgramInfo {
  id: string;                         // "etc" | "social-studies" | ...
  // Names
  nameTh: string;                     // "เทคโนโลยีการศึกษาและคอมพิวเตอร์ศึกษา"
  nameEn?: string;
  degree: string;                     // "กศ.บ." | "กศ.ม." | "กศ.ด." | "ศ.บ." | ...
  degreeNameTh: string;               // "การศึกษาบัณฑิต"
  // Org hierarchy
  department: string;                 // "ภาควิชาเทคโนโลยีและสื่อสารการศึกษา"
  faculty: string;                    // "คณะศึกษาศาสตร์"
  facultyEn?: string;
  university: string;                 // "มหาวิทยาลัยมหาสารคาม"
  universityEn?: string;
  // Contact
  phone?: string;                     // "0-4372-1764 ต่อ 6235"
  docNoPrefix?: string;               // "อว 0605.5(6)/"
  email?: string;
  // Signatories (for printed docs)
  chairName: string;                  // "ผศ.ดร.มานิตย์ อาษานอก"
  chairTitle?: string;                // "ประธานหลักสูตร"
  chairUid?: string;                  // link to user
  chairSignatureUrl?: string;         // path to signature PNG
  deptHeadName?: string;              // "ผศ.ดร.ธนดล ภูสีฤทธิ์"
  deptHeadTitle?: string;             // "หัวหน้าภาควิชา"
  deptHeadUid?: string;
  deptHeadSignatureUrl?: string;
  // System
  systemUrl?: string;                 // "https://verification-msu.web.app/"
  meetUrl?: string;                   // default Google Meet link
  location?: string;                  // default onsite address
  // Meta
  active: boolean;                    // can be deactivated without deletion
  curriculumYear?: number;            // current active curriculum (2562 / 2567 / ...)
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  // Color / Icon for UI distinction
  primaryColor?: string;              // hex code, e.g. "#dc2626"
  emoji?: string;                     // "💻" | "🌏" | etc.
}

// ============ Hooks ============

/** Read a single program's metadata. */
export function useProgramInfo(programId: string | null) {
  return useQuery({
    queryKey: ["program-info", programId],
    enabled: !!programId,
    queryFn: async (): Promise<ProgramInfo | null> => {
      if (!programId) return null;
      const { db } = getFirebase();
      const snap = await getDoc(doc(db, "programs", programId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Omit<ProgramInfo, "id">) };
    },
  });
}

/** List all programs the user has access to. If uid given, filters by user.programs[]. */
export function useAllPrograms() {
  return useQuery({
    queryKey: ["all-programs"],
    queryFn: async (): Promise<ProgramInfo[]> => {
      const { db } = getFirebase();
      const snap = await getDocs(query(collection(db, "programs"), where("active", "==", true)));
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ProgramInfo, "id">) }));
    },
  });
}

/** Create or update a program doc. */
export function useUpdateProgramInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { programId: string; info: Partial<ProgramInfo> }) => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", args.programId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        await setDoc(ref, {
          ...args.info,
          active: args.info.active ?? true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        await updateDoc(ref, {
          ...args.info,
          updatedAt: serverTimestamp(),
        });
      }
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["program-info", vars.programId] });
      qc.invalidateQueries({ queryKey: ["all-programs"] });
    },
  });
}

/**
 * Delete a program document (NOT its subcollections — courses/curriculum/etc.
 * remain orphaned in Firestore and must be cleaned manually if desired).
 * Used by admin/programs page to remove stray/duplicate program entries.
 */
export function useDeleteProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (programId: string) => {
      const { db } = getFirebase();
      await deleteDoc(doc(db, "programs", programId));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all-programs"] });
      qc.invalidateQueries({ queryKey: ["program-info"] });
    },
  });
}

/** Default fallback for pages that haven't yet read from Firestore (or for new programs). */
export const DEFAULT_PROGRAM_INFO: ProgramInfo = {
  id: "",
  nameTh: "(ไม่ระบุชื่อหลักสูตร)",
  degree: "กศ.บ.",
  degreeNameTh: "การศึกษาบัณฑิต",
  department: "—",
  faculty: "คณะศึกษาศาสตร์",
  university: "มหาวิทยาลัยมหาสารคาม",
  universityEn: "Mahasarakham University",
  chairName: "—",
  chairTitle: "ประธานหลักสูตร",
  deptHeadName: "—",
  deptHeadTitle: "หัวหน้าภาควิชา",
  systemUrl: "https://verification-msu.web.app/",
  active: true,
};

/**
 * Hard-coded fallbacks per program ID. Used when the Firestore /programs/{id}
 * doc has not been seeded yet (or doesn't exist for this program). Pages
 * should pass the result of `useProgramInfo()` through `withFallback(id, info)`
 * to guarantee a usable PROGRAM_INFO object even before Firestore is populated.
 */
const FALLBACK_PROGRAMS: Record<string, ProgramInfo> = {
  "demo-cs": {
    id: "demo-cs",
    nameTh: "เทคโนโลยีการศึกษาและคอมพิวเตอร์ศึกษา",
    nameEn: "Educational Technology and Computer Education",
    degree: "กศ.บ.",
    degreeNameTh: "การศึกษาบัณฑิต",
    department: "ภาควิชาเทคโนโลยีและสื่อสารการศึกษา",
    faculty: "คณะศึกษาศาสตร์",
    facultyEn: "Faculty of Education",
    university: "มหาวิทยาลัยมหาสารคาม",
    universityEn: "Mahasarakham University",
    phone: "0-4372-1764 ต่อ 6235",
    docNoPrefix: "อว 0605.5(6)/",
    chairName: "ผู้ช่วยศาสตราจารย์ ดร.มานิตย์ อาษานอก",
    chairTitle: "ประธานหลักสูตร",
    deptHeadName: "ผู้ช่วยศาสตราจารย์ ดร.ธนดล ภูสีฤทธิ์",
    deptHeadTitle: "หัวหน้าภาควิชา",
    systemUrl: "https://verification-msu.web.app/",
    meetUrl: "https://meet.google.com/sks-bbab-koa",
    location: "สำนักงานภาควิชาฯ อาคารศึกษาศาสตร์ คณะศึกษาศาสตร์ มหาวิทยาลัยมหาสารคาม",
    active: true,
    primaryColor: "#dc2626",
    emoji: "💻",
  },
  "social-studies": {
    id: "social-studies",
    nameTh: "การศึกษา (วิชาเอกสังคมศึกษา)",
    nameEn: "Education (Social Studies Major)",
    degree: "กศ.บ.",
    degreeNameTh: "การศึกษาบัณฑิต",
    department: "ภาควิชาสังคมวิทยาและมานุษยวิทยา",
    faculty: "คณะศึกษาศาสตร์",
    facultyEn: "Faculty of Education",
    university: "มหาวิทยาลัยมหาสารคาม",
    universityEn: "Mahasarakham University",
    docNoPrefix: "อว 0605.5/",
    chairName: "(โปรดระบุชื่อประธานหลักสูตร)",
    chairTitle: "ประธานหลักสูตร",
    deptHeadName: "(โปรดระบุชื่อหัวหน้าภาควิชา)",
    deptHeadTitle: "หัวหน้าภาควิชา",
    systemUrl: "https://verification-msu.web.app/",
    location: "คณะศึกษาศาสตร์ มหาวิทยาลัยมหาสารคาม",
    active: true,
    primaryColor: "#16a34a",
    emoji: "🌏",
  },
};

/**
 * Resolve a usable ProgramInfo for a given id. Priority:
 *   1) Firestore doc (if loaded)
 *   2) Hard-coded fallback for known programs
 *   3) DEFAULT_PROGRAM_INFO
 *
 * Use this in document pages to avoid empty parentheses / missing chair names.
 */
export function resolveProgramInfo(id: string, firestoreInfo: ProgramInfo | null | undefined): ProgramInfo {
  if (firestoreInfo && firestoreInfo.chairName && firestoreInfo.chairName !== "—") {
    return firestoreInfo;
  }
  const fb = FALLBACK_PROGRAMS[id];
  if (fb) return fb;
  return { ...DEFAULT_PROGRAM_INFO, id };
}

// Suppress unused import warning if certain functions are only used by callers
void Timestamp;
void orderBy;
